import { WebSocketMessage, WebSocketConnection, WebSocketConfig, RealTimeEvent } from '../types';

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private connection: WebSocketConnection;
  private listeners: Map<string, Set<(message: WebSocketMessage) => void>> = new Map();
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private isManuallyDisconnected = false;

  constructor(config: WebSocketConfig) {
    this.config = config;
    this.connection = {
      isConnected: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: config.maxReconnectAttempts,
      reconnectDelay: config.reconnectDelay,
    };
  }

  /**
   * Connect to WebSocket server
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.isManuallyDisconnected = false;
        
        // Construct WebSocket URL with auth if provided
        let url = this.config.url;
        if (this.config.auth?.token) {
          const urlObj = new URL(url);
          urlObj.searchParams.set('token', this.config.auth.token);
          if (this.config.auth.userId) {
            urlObj.searchParams.set('userId', this.config.auth.userId);
          }
          url = urlObj.toString();
        }

        this.ws = new WebSocket(url, this.config.protocols);

        this.ws.onopen = () => {
          this.connection.isConnected = true;
          this.connection.lastConnected = new Date().toISOString();
          this.connection.reconnectAttempts = 0;
          
          this.startHeartbeat();
          this.emit('connection', { 
            type: 'connection', 
            timestamp: new Date().toISOString(),
            data: { status: 'connected' }
          });
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          this.connection.isConnected = false;
          this.stopHeartbeat();
          
          this.emit('connection', {
            type: 'connection',
            timestamp: new Date().toISOString(),
            data: { 
              status: 'disconnected', 
              code: event.code, 
              reason: event.reason 
            }
          });

          // Attempt reconnection if not manually disconnected
          if (!this.isManuallyDisconnected && this.config.reconnect) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.emit('error', {
            type: 'error',
            timestamp: new Date().toISOString(),
            data: { error: 'WebSocket connection error' }
          });
          
          if (!this.connection.isConnected) {
            reject(new Error('Failed to connect to WebSocket server'));
          }
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isManuallyDisconnected = true;
    this.stopHeartbeat();
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close(1000, 'Manual disconnect');
    }
    
    this.ws = null;
    this.connection.isConnected = false;
  }

  /**
   * Send message to server
   */
  send(message: Omit<WebSocketMessage, 'timestamp'>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    const fullMessage: WebSocketMessage = {
      ...message,
      timestamp: new Date().toISOString(),
    };

    this.ws.send(JSON.stringify(fullMessage));
  }

  /**
   * Subscribe to specific message types
   */
  subscribe(type: string, callback: (message: WebSocketMessage) => void): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    
    this.listeners.get(type)!.add(callback);
    
    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(type);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.listeners.delete(type);
        }
      }
    };
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): WebSocketConnection {
    return { ...this.connection };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<WebSocketConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  private handleMessage(message: WebSocketMessage): void {
    // Emit to specific type listeners
    this.emit(message.type, message);
    
    // Emit to general listeners
    this.emit('message', message);
  }

  private emit(type: string, message: WebSocketMessage): void {
    const listeners = this.listeners.get(type);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(message);
        } catch (error) {
          console.error(`Error in WebSocket listener for type ${type}:`, error);
        }
      });
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({
          type: 'metrics',
          data: { action: 'ping' }
        });
      }
    }, this.config.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.connection.reconnectAttempts >= this.connection.maxReconnectAttempts) {
      console.warn('Max reconnection attempts reached');
      this.emit('error', {
        type: 'error',
        timestamp: new Date().toISOString(),
        data: { error: 'Max reconnection attempts reached' }
      });
      return;
    }

    const delay = this.connection.reconnectDelay * Math.pow(2, this.connection.reconnectAttempts);
    this.connection.reconnectAttempts++;

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.connection.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch(error => {
        console.error('Reconnection failed:', error);
      });
    }, delay);
  }
}

/**
 * WebSocket manager for the application
 */
export class WebSocketManager {
  private static instance: WebSocketManager | null = null;
  private services: Map<string, WebSocketService> = new Map();
  private defaultConfig: WebSocketConfig;

  private constructor() {
    // Default WebSocket configuration
    this.defaultConfig = {
      url: this.getWebSocketUrl(),
      reconnect: true,
      maxReconnectAttempts: 5,
      reconnectDelay: 1000,
      heartbeatInterval: 30000, // 30 seconds
    };
  }

  static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  /**
   * Create or get WebSocket service for a specific channel
   */
  getService(channel: string, config?: Partial<WebSocketConfig>): WebSocketService {
    if (!this.services.has(channel)) {
      const serviceConfig = { 
        ...this.defaultConfig, 
        ...config,
        url: `${this.defaultConfig.url}/${channel}`
      };
      
      this.services.set(channel, new WebSocketService(serviceConfig));
    }
    
    return this.services.get(channel)!;
  }

  /**
   * Connect to all active services
   */
  async connectAll(): Promise<void> {
    const promises = Array.from(this.services.values()).map(service => 
      service.connect().catch(error => {
        console.error('Failed to connect service:', error);
      })
    );
    
    await Promise.all(promises);
  }

  /**
   * Disconnect all services
   */
  disconnectAll(): void {
    this.services.forEach(service => service.disconnect());
  }

  /**
   * Get connection status for all services
   */
  getOverallStatus(): {
    connected: number;
    total: number;
    services: Record<string, WebSocketConnection>;
  } {
    const services: Record<string, WebSocketConnection> = {};
    let connected = 0;
    
    this.services.forEach((service, channel) => {
      const status = service.getConnectionStatus();
      services[channel] = status;
      if (status.isConnected) connected++;
    });

    return {
      connected,
      total: this.services.size,
      services,
    };
  }

  private getWebSocketUrl(): string {
    // Convert HTTP URL to WebSocket URL
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    const wsUrl = baseUrl.replace(/^http/, 'ws');
    return `${wsUrl}/ws`;
  }
}

// Export singleton instance
export const webSocketManager = WebSocketManager.getInstance();